import { useEffect, useState, useCallback, useMemo } from 'react';
import { Anchor, AlertTriangle, ChevronDown, ChevronUp, Save, Bluetooth, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { saveRig, backendConfigToRig } from '../services/oarlock/riggingStorage';
import { setLastPaired } from '../services/oarlock/pairingMemory';
import OarlockGattInspector from './OarlockGattInspector';
import PinCalibrationGuide from './PinCalibrationGuide';
import { calibrationVerdict, STATUS_TONE } from '../services/oarlock/calibrationReminder';
import useOarlockBleConnection from '../hooks/useOarlockBleConnection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VENDOR_META = {
  nk_empower: { label: 'NK EmPower Oarlock', accent: 'text-amber-600 dark:text-amber-400' },
  xboat: { label: 'XBoat Oarlock', accent: 'text-sky-600 dark:text-sky-400' },
};

// Sculling boats use 2 gates (port + starboard); sweep boats use 1.
const SCULLING_BOATS = new Set(['1x', '2x', '4x', 'c2_erg']);
const sidesForBoat = (boatType, singleSculling) => {
  if (boatType && SCULLING_BOATS.has(boatType)) {
    return singleSculling ? ['single_scull'] : ['port', 'starboard'];
  }
  return ['sweep'];
};

const SIDE_LABELS = {
  sweep: 'Sweep oarlock',
  port: 'Bowside',
  starboard: 'Strokeside',
  single_scull: 'Single sculling oarlock',
};

const BATTERY_OPTIONS = [
  { value: '', label: '—' },
  { value: 'lithium', label: 'Lithium' },
  { value: 'alkaline', label: 'Alkaline' },
  { value: 'nimh', label: 'NiMH' },
  { value: 'rechargeable', label: 'Rechargeable' },
];

// Default SP (Spread) by rig type — sweep ~85-90cm, sculling ~80cm/side.
const DEFAULT_SPREAD = (side) => (side === 'sweep' ? 87.0 : 80.0);

const DEFAULT_FORM = (side) => ({
  side,
  boat_id: 'BOAT1',
  seat_number: 1,
  oar_length_cm: 288.0,
  inboard_cm: 88.0,
  spread_cm: DEFAULT_SPREAD(side),
  set_zero_force: '',
  set_zero_angle: '',
  pin_calibration: false,
  pin_cal_steps: [],
  pin_cal_completed_at: null,
  // MFW2 defaults to true for sweep AND single-sculler cases (one gate measuring half the boat).
  multiply_fwp_by_2: side === 'sweep' || side === 'single_scull',
  battery_type: '',
});

function NumberField({ label, abbr, value, onChange, step = 0.5, suffix, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
        {abbr} <span className="text-zinc-400 dark:text-zinc-500 font-medium normal-case tracking-normal">· {label}</span>
      </span>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="number"
          step={step}
          value={value === null || value === undefined ? '' : value}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          className="w-full px-3 py-2 rounded-lg border-2 border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white text-sm font-mono focus:outline-none focus:border-zinc-900 dark:focus:border-white"
          data-testid={testId}
        />
        {suffix && <span className="text-xs text-zinc-500 dark:text-zinc-400 font-bold">{suffix}</span>}
      </div>
    </label>
  );
}

function TextField({ label, abbr, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
        {abbr} <span className="text-zinc-400 dark:text-zinc-500 font-medium normal-case tracking-normal">· {label}</span>
      </span>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white text-sm font-mono focus:outline-none focus:border-zinc-900 dark:focus:border-white"
        data-testid={testId}
      />
    </label>
  );
}

function CheckboxField({ label, abbr, value, onChange, testId, disabled = false, hint }) {
  return (
    <label className={`flex items-center gap-2 select-none ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={!!value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white focus:ring-zinc-900 dark:focus:ring-white disabled:opacity-60"
        data-testid={testId}
      />
      <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-300">
        {abbr} <span className="text-zinc-500 dark:text-zinc-400 font-medium normal-case tracking-normal">· {label}</span>
        {hint && <span className="block text-[10px] font-normal normal-case tracking-normal text-zinc-400 dark:text-zinc-500 mt-0.5">{hint}</span>}
      </span>
    </label>
  );
}

function SelectField({ label, abbr, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
        {abbr} <span className="text-zinc-400 dark:text-zinc-500 font-medium normal-case tracking-normal">· {label}</span>
      </span>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white text-sm focus:outline-none focus:border-zinc-900 dark:focus:border-white"
        data-testid={testId}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

export default function OarlockDeviceCard({ vendor, comingSoon = false }) {
  if (comingSoon) return <OarlockComingSoonCard vendor={vendor} />;
  return <OarlockDeviceCardInner vendor={vendor} />;
}

function OarlockComingSoonCard({ vendor }) {
  const meta = VENDOR_META[vendor];
  return (
    <div
      className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm md:col-span-2 transition-colors opacity-80"
      data-testid={`${vendor}-device-section`}
    >
      <div className="w-full p-4 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-zinc-100 dark:bg-zinc-700 flex items-center justify-center shrink-0">
            <Anchor className={`w-4 h-4 ${meta?.accent || 'text-zinc-400'}`} />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-zinc-900 dark:text-white truncate" style={{ fontFamily: 'Archivo, sans-serif' }}>
              {meta?.label || vendor}
            </h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
              Smart-gate oarlock support
            </p>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-100 dark:bg-zinc-700 text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400"
          data-testid={`${vendor}-coming-soon-badge`}
        >
          <Bluetooth className="w-3 h-3" />
          Coming soon
        </span>
      </div>
    </div>
  );
}

function OarlockDeviceCardInner({ vendor }) {
  const meta = VENDOR_META[vendor];
  const [expanded, setExpanded] = useState(false);
  // Subscribe to the BLE driver so the collapsed-card header reflects live
  // connection state (mirrors PM5 / HR cards). The full GATT inspector
  // (discovery + live byte capture) still lives inside the expanded body.
  const { status: bleStatus, busy: bleBusy, supported: bleSupported, connect: bleConnect, disconnect: bleDisconnect } = useOarlockBleConnection(vendor);

  // Persisted "gate side" — which physical side of the boat this oarlock is
  // installed on. Determines which side's metric tiles (PF-P / CA-P vs
  // PF-S / CA-S) light up from streamed strokes. Defaults to 'sweep' which
  // mirrors data onto both side tiles for sweep / single-scull rigs.
  const gateSideStorageKey = `oarlock_gate_side_${vendor}`;
  const [gateSide, setGateSide] = useState(() => {
    try { return localStorage.getItem(gateSideStorageKey) || 'sweep'; }
    catch { return 'sweep'; }
  });
  const updateGateSide = (next) => {
    setGateSide(next);
    try { localStorage.setItem(gateSideStorageKey, next); } catch { /* noop */ }
  };

  const handleHeaderConnect = async (e) => {
    e.stopPropagation();
    if (!bleSupported) {
      toast.error('Web Bluetooth not available. Use Chrome / Edge on Android or desktop, or Bluefy on iOS.');
      return;
    }
    // Pass the configured side so `startStream` tags every per-stroke event
    // with the right side label (port / starboard / sweep / single_scull).
    const r = await bleConnect({ side: gateSide });
    if (r.ok) {
      toast.success(`Connected to ${r.info?.name || meta.label} · ${gateSide}`);
      setExpanded(true);   // open the card so the user sees Device Info + Capture
    } else if (!/NotFoundError|User cancelled/i.test(r.error || '')) {
      toast.error(`Connect failed: ${r.error}`);
    } else {
      // Filtered chooser was empty → expand and show the "Show all" fallback.
      toast('Oarlock not in the list?', {
        description: 'Expand the card and tap "Show all" — or unpair from your phone\'s system Bluetooth first.',
        duration: 8000,
      });
      setExpanded(true);
    }
  };

  const handleHeaderDisconnect = async (e) => {
    e.stopPropagation();
    await bleDisconnect();
    toast.success('Disconnected');
  };
  const [boatType, setBoatType] = useState(() => localStorage.getItem('default_boat_type') || 'c2_erg');
  const isSculling = !!boatType && SCULLING_BOATS.has(boatType);
  const singleSculllStorageKey = `oarlock_single_scull_${vendor}`;
  const [singleSculling, setSingleSculling] = useState(() => {
    try { return localStorage.getItem(singleSculllStorageKey) === 'true'; } catch { return false; }
  });
  const [forms, setForms] = useState(() => {
    const sides = sidesForBoat(boatType, singleSculling);
    return Object.fromEntries(sides.map(s => [s, DEFAULT_FORM(s)]));
  });
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);

  // Re-detect boat class when localStorage changes (boat picker on home screen)
  useEffect(() => {
    const onStorage = () => {
      const bt = localStorage.getItem('default_boat_type') || 'c2_erg';
      setBoatType(bt);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Reset form skeletons when boat class flips between sweep and sculling, or single-sculling toggles
  useEffect(() => {
    const sides = sidesForBoat(boatType, singleSculling);
    setForms(prev => {
      const next = {};
      sides.forEach(s => { next[s] = prev[s] || DEFAULT_FORM(s); });
      return next;
    });
  }, [boatType, singleSculling]);

  // Persist single-sculling toggle
  useEffect(() => {
    try { localStorage.setItem(singleSculllStorageKey, singleSculling ? 'true' : 'false'); } catch {}
  }, [singleSculling, singleSculllStorageKey]);

  // Hydrate from backend
  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/oarlocks?vendor=${vendor}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const list = await res.json();
      setForms(prev => {
        const next = { ...prev };
        list.forEach(cfg => {
          if (next[cfg.side]) {
            next[cfg.side] = {
              side: cfg.side,
              boat_id: cfg.boat_id,
              seat_number: cfg.seat_number,
              oar_length_cm: cfg.oar_length_cm,
              inboard_cm: cfg.inboard_cm,
              spread_cm: cfg.spread_cm ?? DEFAULT_SPREAD(cfg.side),
              set_zero_force: cfg.set_zero_force ?? '',
              set_zero_angle: cfg.set_zero_angle ?? '',
              pin_calibration: !!cfg.pin_calibration,
              pin_cal_steps: Array.isArray(cfg.pin_cal_steps) ? cfg.pin_cal_steps : [],
              pin_cal_completed_at: cfg.pin_cal_completed_at || null,
              multiply_fwp_by_2: !!cfg.multiply_fwp_by_2,
              battery_type: cfg.battery_type || '',
              config_id: cfg.config_id,
            };
            // Hydrate localStorage cache so algorithms work offline / pre-login
            try { saveRig(cfg.boat_id, cfg.seat_number, backendConfigToRig(cfg)); } catch (_e) { /* noop */ }
          }
        });
        return next;
      });
    } catch (e) {
      // Silent on first load - empty config is valid
      console.warn('[OarlockDeviceCard] load failed:', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [vendor]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const updateField = (side, key, value) => {
    setForms(prev => ({ ...prev, [side]: { ...prev[side], [key]: value } }));
  };

  const saveSide = async (side) => {
    const f = forms[side];
    if (!f) return;
    setSaving(s => ({ ...s, [side]: true }));
    try {
      const mfw2Locked = side === 'sweep' || side === 'single_scull';
      const payload = {
        vendor,
        boat_id: f.boat_id || 'BOAT1',
        seat_number: Number(f.seat_number) || 1,
        side,
        oar_length_cm: Number(f.oar_length_cm),
        inboard_cm: Number(f.inboard_cm),
        spread_cm: Number(f.spread_cm ?? DEFAULT_SPREAD(side)),
        set_zero_force: f.set_zero_force === '' ? null : Number(f.set_zero_force),
        set_zero_angle: f.set_zero_angle === '' ? null : Number(f.set_zero_angle),
        pin_calibration: !!f.pin_calibration,
        pin_cal_steps: Array.isArray(f.pin_cal_steps) ? f.pin_cal_steps : [],
        pin_cal_completed_at: f.pin_cal_completed_at || null,
        multiply_fwp_by_2: mfw2Locked ? true : !!f.multiply_fwp_by_2,
        battery_type: f.battery_type || null,
      };
      const res = await fetch(`${API}/oarlocks`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const saved = await res.json();
      setForms(prev => ({ ...prev, [side]: { ...prev[side], config_id: saved.config_id } }));
      // Write-through to localStorage so algorithms (Layer 4) work offline / pre-login.
      try { saveRig(saved.boat_id, saved.seat_number, backendConfigToRig(saved)); } catch (_e) { /* noop */ }
      // Remember as the most recently paired smart-gate so the Monitor page can
      // offer a one-tap "use last device?" toast next session.
      try {
        setLastPaired({
          vendor,
          boatId: saved.boat_id,
          seatNumber: saved.seat_number,
          side: saved.side,
          displayName: `${meta.label} · ${side}`,
        });
      } catch (_e) { /* noop */ }
      toast.success(`${meta.label} · ${side} saved`);
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(s => ({ ...s, [side]: false }));
    }
  };

  const sides = sidesForBoat(boatType, singleSculling);
  const rigLabel = (
    !isSculling
      ? 'Sweep rig · 1 oarlock'
      : singleSculling
        ? 'Sculling rig · 1 oarlock (data ×2)'
        : 'Sculling rig · 2 oarlocks'
  );

  // Build the rig map the BLE driver needs (ZF/ZA/MFW2 per side) from form state.
  const sidesKey = sides.join(',');
  const driverRigBySide = useMemo(() => {
    const map = {};
    for (const s of sides) {
      const f = forms[s] || {};
      map[s] = {
        ol: Number(f.oar_length_cm),
        ib: Number(f.inboard_cm),
        sp: Number(f.spread_cm),
        zf: f.set_zero_force === '' ? 0 : Number(f.set_zero_force),
        za: f.set_zero_angle === '' ? 0 : Number(f.set_zero_angle),
        pcal: !!f.pin_calibration,
        mfw2: !!f.multiply_fwp_by_2,
        side: s,
      };
    }
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms, sidesKey]);

  return (
    <div
      className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm md:col-span-2 transition-colors"
      data-testid={`${vendor}-device-section`}
    >
      <div
        className="w-full p-4 flex items-center justify-between text-left"
        data-testid={`${vendor}-toggle`}
      >
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-90 transition text-left"
          data-testid={`${vendor}-toggle-btn`}
        >
          <div className="w-9 h-9 rounded-lg bg-zinc-100 dark:bg-zinc-700 flex items-center justify-center shrink-0">
            <Anchor className={`w-4 h-4 ${meta.accent}`} />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-zinc-900 dark:text-white truncate" style={{ fontFamily: 'Archivo, sans-serif' }}>
              {meta.label}
            </h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
              {rigLabel}
            </p>
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {/* Live BLE status — matches HR/PM5 cards. Replaces the old "Pairing soon" */}
          {bleStatus.connected ? (
            <>
              <span
                className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/40 text-[10px] font-bold uppercase tracking-wider text-green-700 dark:text-green-400"
                data-testid={`${vendor}-pairing-status`}
              >
                <CheckCircle className="w-3 h-3" />
                {bleStatus.deviceName ? `${bleStatus.deviceName} · ${gateSide}` : `Connected · ${gateSide}`}
              </span>
              <button
                type="button"
                onClick={handleHeaderDisconnect}
                disabled={bleBusy}
                data-testid={`${vendor}-header-disconnect-btn`}
                className="px-2.5 py-1 rounded-md bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 text-[10px] font-bold uppercase tracking-wider hover:bg-rose-100 dark:hover:bg-rose-900/50 transition disabled:opacity-50"
              >
                Disconnect
              </button>
            </>
          ) : (
            <>
              {/* Side selector — pin this gate to port / starboard / sweep
                  so the per-stroke events carry the right side label. Hidden
                  once connected (the choice is locked at startStream time). */}
              <div className="flex flex-col items-end gap-1">
                <select
                  value={gateSide}
                  onChange={(e) => { e.stopPropagation(); updateGateSide(e.target.value); }}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`${vendor}-header-side-select`}
                  className="px-2 py-1 rounded-md bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 dark:border-zinc-600 focus:outline-none focus:ring-2 focus:ring-zinc-400"
                  title="Which side of the boat is this oarlock installed on?"
                >
                  <option value="sweep">Sweep</option>
                  <option value="port">Bowside</option>
                  <option value="starboard">Strokeside</option>
                  <option value="single_scull">1x</option>
                </select>
                {isSculling && gateSide === 'sweep' && (
                  <div
                    data-testid={`${vendor}-dual-gate-hint`}
                    className="text-xs text-zinc-400 dark:text-zinc-500 max-w-[220px] text-right leading-snug"
                  >
                    For dual-gate sculling, select Bowside or Strokeside before connecting. Sweep mode mirrors data to both sides.
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleHeaderConnect}
                disabled={bleBusy || !bleSupported}
                data-testid={`${vendor}-header-connect-btn`}
                title={!bleSupported ? 'Web Bluetooth unavailable in this browser' : `Will connect as ${gateSide}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-[10px] font-bold uppercase tracking-wider hover:opacity-90 transition disabled:opacity-50"
              >
                <Bluetooth className="w-3 h-3" />
                {bleBusy ? 'Pairing…' : 'Connect'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            data-testid={`${vendor}-chevron-btn`}
            className="p-1 -m-1 hover:opacity-70 transition"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-4" data-testid={`${vendor}-config-body`}>
          <div className="text-xs text-zinc-500 dark:text-zinc-400 bg-zinc-50 dark:bg-zinc-900/40 rounded-lg p-3 border border-zinc-100 dark:border-zinc-700">
            Pair your {meta.label} below to capture the GATT tree (testing phase). Your
            rigging inputs are stored locally + server-side so force/work/power calculations
            are ready the moment streaming UUIDs are wired in.
          </div>

          {/* Web Bluetooth Connect + GATT inspector. Lives here so the user can
              pair the device once and report the discovered UUIDs back. The
              driver receives the current rig map so ZF/ZA correction applies
              as soon as streaming UUIDs are configured. */}
          <OarlockGattInspector
            vendor={vendor}
            vendorLabel={meta.label}
            rigBySide={driverRigBySide}
          />

          {isSculling && (
            <label
              className="flex items-start gap-2 cursor-pointer select-none p-3 rounded-lg bg-zinc-100 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-700"
              data-testid={`${vendor}-single-scull-toggle-label`}
            >
              <input
                type="checkbox"
                checked={singleSculling}
                onChange={(e) => setSingleSculling(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white"
                data-testid={`${vendor}-single-scull-toggle`}
              />
              <span className="flex-1">
                <span className="block text-xs font-bold uppercase tracking-wider text-zinc-900 dark:text-white">
                  Single oarlock on sculling rig
                </span>
                <span className="block text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5">
                  Most scullers own one smart gate. Enable this to record one oarlock — force,
                  work, and power will be doubled (MFW2 forced ON) to estimate full-boat output.
                </span>
              </span>
            </label>
          )}

          {sides.map(side => {
            const f = forms[side] || DEFAULT_FORM(side);
            const sideLabel = SIDE_LABELS[side] || side;
            const mfw2Locked = side === 'sweep' || side === 'single_scull';
            const calVerdict = calibrationVerdict({
              completedAt: f.pin_cal_completed_at,
              currentFwRev: null,    // wired once Device Info auto-read lands
              lastFwRev: null,
            });
            const isDue = calVerdict.status === 'due' || calVerdict.status === 'stale' || calVerdict.status === 'never';
            const tone = STATUS_TONE[calVerdict.status] || STATUS_TONE.never;
            return (
              <div
                key={side}
                className="border-2 border-zinc-200 dark:border-zinc-700 rounded-xl p-4 bg-zinc-50/40 dark:bg-zinc-900/20"
                data-testid={`${vendor}-${side}-form`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-zinc-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                    {sideLabel}
                    {/* Calibration status dot — amber=due, rose=overdue, zinc=never */}
                    {calVerdict.status !== 'fresh' && (
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${tone.dot}`}
                        title={calVerdict.label}
                        data-testid={`${vendor}-${side}-cal-dot`}
                      />
                    )}
                  </h4>
                  <button
                    type="button"
                    onClick={() => saveSide(side)}
                    disabled={!!saving[side] || loading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-xs font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-50 transition"
                    data-testid={`${vendor}-${side}-save`}
                  >
                    <Save className="w-3.5 h-3.5" />
                    {saving[side] ? 'Saving…' : 'Save'}
                  </button>
                </div>

                {/* Calibration-due reminder banner — shown when status != fresh */}
                {isDue && (
                  <div
                    className={`mb-3 px-3 py-2 rounded-lg border ${tone.bg} ${tone.text} flex items-start gap-2 text-[11px]`}
                    data-testid={`${vendor}-${side}-cal-reminder`}
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-bold uppercase tracking-wider">
                        {calVerdict.label}
                        {typeof calVerdict.daysSince === 'number' && calVerdict.daysSince > 0 && (
                          <span className="ml-1 font-normal lowercase tracking-normal">
                            · {calVerdict.daysSince} days ago
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 opacity-90">
                        {calVerdict.status === 'never'
                          ? 'No calibration record yet. Tick the PCAL checkbox below to start the 6-step walkthrough.'
                          : calVerdict.status === 'stale'
                            ? 'Force readings may drift. Re-run the PCAL walkthrough before your next session.'
                            : 'Time for a refresh — tick PCAL below to re-run the walkthrough.'}
                        {calVerdict.detail && <span className="block mt-0.5 italic opacity-80">{calVerdict.detail}</span>}
                      </p>
                    </div>
                    {!f.pin_calibration && (
                      <button
                        type="button"
                        onClick={() => updateField(side, 'pin_calibration', true)}
                        className={`shrink-0 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${tone.bg} ${tone.text} ring-1 ${tone.ring} hover:opacity-90 transition`}
                        data-testid={`${vendor}-${side}-cal-reminder-action`}
                      >
                        Calibrate now
                      </button>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <NumberField label="Oar Length"   abbr="OL"   value={f.oar_length_cm} onChange={v => updateField(side, 'oar_length_cm', v)} step={0.5} suffix="cm"  testId={`${vendor}-${side}-oar-length`} />
                  <NumberField label="Inboard"      abbr="IB"   value={f.inboard_cm}    onChange={v => updateField(side, 'inboard_cm', v)}    step={0.5} suffix="cm"  testId={`${vendor}-${side}-inboard`} />
                  <NumberField label="Spread"       abbr="SP"   value={f.spread_cm}     onChange={v => updateField(side, 'spread_cm', v)}     step={0.5} suffix="cm"  testId={`${vendor}-${side}-spread`} />
                  <TextField   label="Boat ID"      abbr="BTID" value={f.boat_id}       onChange={v => updateField(side, 'boat_id', v)}                              testId={`${vendor}-${side}-boat-id`} />
                  <NumberField label="Seat Number"  abbr="STN"  value={f.seat_number}   onChange={v => updateField(side, 'seat_number', v)}   step={1}              testId={`${vendor}-${side}-seat-number`} />
                  <NumberField label="Zero Force"   abbr="ZF"   value={f.set_zero_force} onChange={v => updateField(side, 'set_zero_force', v)} step={0.1} suffix="N"  testId={`${vendor}-${side}-zero-force`} />
                  <NumberField label="Zero Angle"   abbr="ZA"   value={f.set_zero_angle} onChange={v => updateField(side, 'set_zero_angle', v)} step={0.1} suffix="°"  testId={`${vendor}-${side}-zero-angle`} />
                  <SelectField label="Battery"      abbr="BATT" value={f.battery_type}   onChange={v => updateField(side, 'battery_type', v)}   options={BATTERY_OPTIONS} testId={`${vendor}-${side}-battery`} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-zinc-200 dark:border-zinc-700">
                  <CheckboxField label="Pin Calibration"      abbr="PCAL" value={f.pin_calibration}    onChange={v => updateField(side, 'pin_calibration', v)}    testId={`${vendor}-${side}-pcal`} />
                  <CheckboxField
                    label="Multiply F,W,P by 2"
                    abbr="MFW2"
                    value={mfw2Locked ? true : f.multiply_fwp_by_2}
                    onChange={v => updateField(side, 'multiply_fwp_by_2', v)}
                    disabled={mfw2Locked}
                    hint={mfw2Locked ? 'Locked ON for one-oarlock setups (force/work/power doubled).' : undefined}
                    testId={`${vendor}-${side}-mfw2`}
                  />
                </div>

                {/* Pin Calibration walkthrough — shown when PCAL is checked.
                    Each step toggle updates form state; the existing per-side Save
                    button persists the array + completed-at timestamp. */}
                {f.pin_calibration && (
                  <PinCalibrationGuide
                    vendor={vendor}
                    side={side}
                    completedSteps={f.pin_cal_steps || []}
                    completedAt={f.pin_cal_completed_at}
                    onChange={(steps, doneAt) => {
                      setForms(prev => ({
                        ...prev,
                        [side]: { ...prev[side], pin_cal_steps: steps, pin_cal_completed_at: doneAt },
                      }));
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
